"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  Play,
  Clock,
  ArrowRight,
  CheckCircle2,
  User2,
  Lightbulb,
  Target,
} from "lucide-react";
import Image from "next/image";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: "easeOut" },
  }),
};

function useCountdown(minutes: number) {
  const [secondsLeft, setSecondsLeft] = useState(minutes * 60);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const interval = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [secondsLeft]);

  const hrs = Math.floor(secondsLeft / 3600);
  const mins = Math.floor((secondsLeft % 3600) / 60);
  const secs = secondsLeft % 60;
  return { hrs, mins, secs, expired: secondsLeft <= 0 };
}

function TimerBox({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-2xl md:text-3xl font-bold text-violet-600 tabular-nums">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-xs text-gray-500 uppercase tracking-wide">
        {label}
      </span>
    </div>
  );
}

const tabs = [
  {
    id: "método",
    label: "Sobre o método",
    icon: Lightbulb,
  },
  {
    id: "instrutor",
    label: "Sobre o instrutor",
    icon: User2,
  },
  {
    id: "aprender",
    label: "O que você vai aprender",
    icon: Target,
  },
] as const;

export default function WebinarioPage() {
  const [activeTab, setActiveTab] = useState<string>("método");
  const { hrs, mins, secs } = useCountdown(120);

  return (
    <main className="min-h-screen bg-[#faf7f5]">
      {/* Header */}
      <header className="w-full py-4 px-6 border-b border-gray-100">
        <div className="mx-auto max-w-5xl flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-violet-600" />
          <span className="text-lg font-semibold text-gray-900">eBuz</span>
        </div>
      </header>

      <section className="mx-auto max-w-4xl px-4 pt-8 pb-16 md:pt-12">
        {/* Title */}
        <motion.div
          className="text-center mb-8"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 px-4 py-1.5 text-sm font-medium text-violet-700 mb-4">
            <Play className="h-3.5 w-3.5" />
            Webinário exclusivo
          </span>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Arquitetura de Produtos Educacionais - O Método
          </h1>
        </motion.div>

        {/* Video Player */}
        <motion.div
          className="relative aspect-video rounded-2xl bg-gray-900 overflow-hidden shadow-xl mb-6"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          {/* Placeholder - replace with actual YouTube embed */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/80">
            <div className="w-20 h-20 rounded-full bg-violet-600 flex items-center justify-center mb-4 hover:bg-violet-500 transition-colors cursor-pointer">
              <Play className="h-8 w-8 text-white ml-1" />
            </div>
            <p className="text-lg font-medium">Clique para assistir</p>
            <p className="text-sm text-white/50 mt-1 flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              45 minutos
            </p>
          </div>
        </motion.div>

        {/* Urgency Timer */}
        <motion.div
          className="rounded-xl bg-white border border-violet-200 p-4 md:p-6 mb-10 text-center"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          <p className="text-sm text-gray-600 mb-3">
            A oferta especial expira em
          </p>
          <div className="flex items-center justify-center gap-4">
            <TimerBox value={hrs} label="horas" />
            <span className="text-xl font-bold text-gray-300">:</span>
            <TimerBox value={mins} label="min" />
            <span className="text-xl font-bold text-gray-300">:</span>
            <TimerBox value={secs} label="seg" />
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          className="text-center mb-12"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={3}
        >
          <a
            href="/oferta"
            className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-8 py-4 text-white font-semibold text-lg hover:bg-violet-700 active:scale-[0.98] transition-all shadow-lg shadow-violet-600/20"
          >
            Quero Me Inscrever no Curso
            <ArrowRight className="h-5 w-5" />
          </a>
        </motion.div>

        {/* Tabs */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={4}
        >
          <div className="flex gap-1 rounded-xl bg-gray-100 p-1 mb-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg py-2.5 px-3 text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Icon className="h-4 w-4 hidden sm:block" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="rounded-xl bg-white border border-gray-100 p-6 md:p-8">
            {activeTab === "método" && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-gray-900">
                  A Metodologia APE
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  A Arquitetura de Produtos Educacionais é um framework baseado
                  em mais de 50 anos de pesquisa científica sobre design
                  instrucional, andragogia e aprendizagem de adultos.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Diferente de métodos improvisados, a APE usa um sistema de 5
                  nós interconectados que garantem que seu produto educacional
                  tenha fundamentação, estrutura e escalabilidade desde o
                  primeiro dia.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  O resultado: produtos que ensinam de verdade, vendem com
                  consistência e funcionam sem depender da sua presença
                  constante.
                </p>
              </div>
            )}

            {activeTab === "instrutor" && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 mb-2">
                  <Image
                    src="/images/jonathan-machado.jpg"
                    alt="Jonathan Machado"
                    width={80}
                    height={80}
                    className="rounded-full border-2 border-violet-200"
                  />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Jonathan Machado
                    </h3>
                    <p className="text-sm text-violet-600">Criador da Metodologia APE</p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Educador, pesquisador e arquiteto de produtos educacionais.
                  Criador da Metodologia APE e fundador da eBuz Negócios
                  Digitais.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Com experiência em design instrucional e tecnologia
                  educacional, Jonathan ajuda experts a transformar conhecimento
                  tácito em produtos estruturados que escalam.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Já publicou 3 cursos com mais de 141 aulas e 125+ alunos na
                  plataforma eBuz, além do livro APE - Arquitetura de Produtos
                  Educacionais.
                </p>
              </div>
            )}

            {activeTab === "aprender" && (
              <div className="space-y-3">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Neste webinário você vai aprender
                </h3>
                {[
                  "Como identificar o conhecimento que você já tem é que pode virar um produto educacional",
                  "O erro fatal que faz 90% dos experts desistirem antes de lançar seu primeiro curso",
                  "O sistema de 5 nos da Metodologia APE e como cada um se conecta",
                  "Como criar uma esteira de produtos que vende sem você precisar estar presente",
                  "A diferença entre um curso improvisado é um produto educacional com arquitetura",
                  "Os primeiros passos práticos para começar ainda esta semana",
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-violet-600 mt-0.5 shrink-0" />
                    <p className="text-gray-600">{item}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </section>
    </main>
  );
}
